import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { usePromptContext, type KindFilter, type SortBy } from '../src/context/PromptContext';
import usePromptActions from '../src/hooks/usePromptActions';
import type { Prompt } from '../types';
import { StarIcon, PlusIcon, MoreVerticalIcon, RestoreIcon, TrashIcon, SortIcon, CheckIcon, KindGlyph } from '../constants';
import { ITEM_KINDS, KIND_META, getKind, excerptFor, getSkillSlug } from '../utils/itemKinds';

const Highlight: React.FC<{ text: string; highlight: string }> = React.memo(function Highlight({ text, highlight }) {
  const term = highlight.trim();
  const parts = useMemo(() => {
    if (!term || !text) return null;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return text.split(new RegExp(`(${escaped})`, 'gi'));
  }, [text, term]);

  if (!parts) return <>{text}</>;
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <mark key={i} className="bg-amber-200/80 dark:bg-amber-400/30 text-inherit rounded-sm px-0.5">{part}</mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
});

const SORT_OPTIONS: { id: SortBy; label: string }[] = [
  { id: 'date-new', label: 'Zuletzt geändert' },
  { id: 'date-old', label: 'Älteste zuerst' },
  { id: 'name-asc', label: 'Name A–Z' },
  { id: 'name-desc', label: 'Name Z–A' },
  { id: 'usage', label: 'Am häufigsten kopiert' },
  { id: 'favorites', label: 'Favoriten zuerst' },
];

const TRASH_MAX_DAYS = 30;

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (diffDays <= 0) return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'gestern';
  if (diffDays < 7) return `vor ${diffDays} Tagen`;
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
};

/** Kurze Strukturangabe je Art: Schritte, Dateien oder Länge */
const structureNote = (p: Prompt) => {
  const kind = getKind(p);
  if (kind === 'workflow') {
    const n = (p.steps || []).length;
    return `${n} ${n === 1 ? 'Schritt' : 'Schritte'}`;
  }
  if (kind === 'gem') {
    const n = (p.files || []).length;
    const words = (p.content || '').trim().split(/\s+/).filter(Boolean).length;
    return n > 0 ? `${words} Wörter · +${n} ${n === 1 ? 'Wissensdatei' : 'Wissensdateien'}` : `${words} ${words === 1 ? 'Wort' : 'Wörter'}`;
  }
  if (kind === 'skill') {
    const n = (p.files || []).length;
    return n > 0 ? `${getSkillSlug(p)} · +${n} ${n === 1 ? 'Datei' : 'Dateien'}` : getSkillSlug(p);
  }
  const words = (p.content || '').trim().split(/\s+/).filter(Boolean).length;
  return `${words} ${words === 1 ? 'Wort' : 'Wörter'}`;
};

const SortMenu: React.FC<{ value: SortBy; onChange: (v: SortBy) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const current = SORT_OPTIONS.find(o => o.id === value);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="icon-btn !w-auto px-2 gap-1.5 text-xs"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Sortierung: ${current?.label}`}
      >
        <SortIcon className="h-4 w-4" />
        <span className="hidden 2xl:inline">{current?.label}</span>
      </button>
      {open && (
        <div className="context-menu absolute right-0 top-full mt-1 w-56 animate-scale-fade-in" role="menu">
          <div className="menu-label">Sortieren nach</div>
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.id}
              role="menuitemradio"
              aria-checked={value === opt.id}
              onClick={() => { onChange(opt.id); setOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-ink hover:bg-ink/[0.06] text-left"
            >
              {opt.label}
              {value === opt.id && <CheckIcon className="h-4 w-4 text-kind-prompt" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PromptList: React.FC = () => {
  const {
    selectedPromptId,
    setSelectedPromptId,
    searchTerm,
    setSearchTerm,
    selectedSpecialView,
    selectedFolderId,
    setModalState,
    kindFilter,
    setKindFilter,
    sortBy,
    setSortBy,
    activeTag,
  } = usePromptContext();

  const actions = usePromptActions();
  const { handleRestorePrompt, displayedPrompts: prompts, getListTitle, kindCounts } = actions;
  const title = getListTitle();
  const isTrash = selectedSpecialView === 'trash';
  const listRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<number | null>(null);
  const isLongPressRef = useRef(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  useEffect(() => {
    return () => cancelLongPress();
  }, [cancelLongPress]);

  const subtitle = useMemo(() => {
    if (isTrash) return `Einträge werden nach ${TRASH_MAX_DAYS} Tagen endgültig gelöscht`;
    if (selectedSpecialView === 'recent') return 'Zuletzt kopiert oder bearbeitet';
    if (activeTag) return 'Alle Einträge mit diesem Tag';
    return null;
  }, [isTrash, selectedSpecialView, activeTag]);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, id: string) => {
    if (isTrash) { e.preventDefault(); return; }
    setDraggedId(id);
    e.dataTransfer.setData('application/json', JSON.stringify({ type: 'prompt', id }));
    e.dataTransfer.effectAllowed = 'move';
  }, [isTrash]);

  const openMenu = useCallback((e: React.MouseEvent | React.TouchEvent, prompt: Prompt) => {
    actions.handleOpenContextMenu(e as React.MouseEvent, 'prompt', prompt.id, { name: prompt.name, isFavorite: prompt.isFavorite, kind: getKind(prompt) });
  }, [actions]);

  const startLongPress = (prompt: Prompt) => (e: React.TouchEvent) => {
    const touch = e.touches[0];
    isLongPressRef.current = false;
    longPressTimer.current = window.setTimeout(() => {
      isLongPressRef.current = true;
      actions.handleOpenContextMenu(
        { preventDefault: () => {}, stopPropagation: () => {}, clientX: touch.clientX, clientY: touch.clientY } as unknown as React.MouseEvent,
        'prompt', prompt.id, { name: prompt.name, isFavorite: prompt.isFavorite, kind: getKind(prompt) },
      );
    }, 550);
  };

  /** Pfeiltasten wandern durch die Liste, Enter öffnet */
  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-list-item]') ?? []);
    const idx = items.indexOf(document.activeElement as HTMLElement);
    const next = items[Math.max(0, Math.min(items.length - 1, idx + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) {
      e.preventDefault();
      next.focus();
      next.scrollIntoView({ block: 'nearest' });
    }
  };

  const filterTabs: { id: KindFilter; label: string }[] = [
    { id: 'all', label: 'Alle' },
    ...ITEM_KINDS.map(k => ({ id: k as KindFilter, label: KIND_META[k].plural })),
  ];

  const emptyState = () => {
    if (searchTerm) {
      return {
        title: `Nichts gefunden für „${searchTerm}“`,
        text: kindFilter !== 'all' ? `Gesucht wurde nur in ${KIND_META[kindFilter].plural}.` : 'Gesucht wird in Titeln, Inhalten, Tags, Schritten und Skill-Dateien.',
        action: (
          <div className="flex gap-2">
            <button onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Suche leeren</button>
            {kindFilter !== 'all' && <button onClick={() => setKindFilter('all')} className="btn btn-ghost btn-sm">In allen Arten suchen</button>}
          </div>
        ),
      };
    }
    if (isTrash) return { title: 'Papierkorb ist leer', text: 'Gelöschte Einträge landen hier und lassen sich 30 Tage lang wiederherstellen.', action: null };
    if (selectedSpecialView === 'favorites') return { title: 'Noch keine Favoriten', text: 'Markieren Sie häufig genutzte Einträge mit dem Stern, dann finden Sie sie hier wieder.', action: null };
    const kind = kindFilter === 'all' ? 'prompt' : kindFilter;
    return {
      title: kindFilter === 'all' ? 'Hier ist noch nichts abgelegt' : `Noch keine ${KIND_META[kind].plural}${selectedFolderId ? ' in diesem Ordner' : ''}`,
      text: KIND_META[kind].hint + '.',
      action: (
        <button onClick={() => actions.handleNewPromptRequest(selectedFolderId, kind)} className="btn btn-primary btn-sm">
          <PlusIcon className="h-4 w-4" /> {KIND_META[kind].newLabel}
        </button>
      ),
    };
  };

  return (
    <div className="flex flex-col h-full bg-sheet">
      {/* Kopf */}
      <div className="shrink-0 px-4 pt-4 pb-0 border-b border-rule">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold text-ink truncate leading-tight">{title}</h2>
            <p className="text-xs text-graphite mt-0.5 truncate">
              {subtitle ?? <span className="font-mono">{prompts.length} {prompts.length === 1 ? 'Eintrag' : 'Einträge'}</span>}
            </p>
          </div>
          <div className="flex items-center gap-0.5 shrink-0 -mr-1">
            {isTrash ? (
              prompts.length > 0 && (
                <button onClick={() => setModalState({ type: 'confirm-empty-trash' })} className="btn btn-ghost btn-sm !text-red-700 dark:!text-red-400">
                  <TrashIcon className="h-4 w-4" /> Leeren
                </button>
              )
            ) : (
              <>
                <SortMenu value={sortBy} onChange={setSortBy} />
                <button
                  onClick={() => actions.handleNewPromptRequest(selectedFolderId)}
                  className="icon-btn"
                  title={kindFilter === 'all' ? 'Neuer Prompt hier' : `${KIND_META[kindFilter].newLabel} hier`}
                  aria-label={kindFilter === 'all' ? 'Neuer Prompt in dieser Ansicht' : `${KIND_META[kindFilter].newLabel} in dieser Ansicht`}
                >
                  <PlusIcon className="h-[18px] w-[18px]" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Art-Filter */}
        <div className="flex gap-4 mt-3 -mb-px overflow-x-auto scrollbar-thin" role="tablist" aria-label="Nach Art filtern">
          {filterTabs.map(tab => {
            const active = kindFilter === tab.id;
            const color = tab.id === 'all' ? 'border-ink' : KIND_META[tab.id].border;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={active}
                onClick={() => setKindFilter(tab.id)}
                className={`shrink-0 flex items-center gap-1.5 pb-2 pt-1 text-[13px] border-b-2 transition-colors ${
                  active ? `${color} text-ink font-medium` : 'border-transparent text-graphite hover:text-ink'
                }`}
              >
                {tab.label}
                <span className="font-mono text-[11px] text-graphite tabular-nums">{kindCounts[tab.id]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Liste */}
      <div ref={listRef} role="list" className="flex-1 overflow-y-auto scrollbar-thin" onKeyDown={handleListKeyDown}>
        {prompts.length === 0 ? (
          (() => {
            const es = emptyState();
            return (
              <div className="px-6 py-14 text-center flex flex-col items-center">
                <div className="flex flex-col gap-1.5 mb-5" aria-hidden="true">
                  <span className="block h-[3px] w-12 rounded bg-kind-prompt/50" />
                  <span className="block h-[3px] w-8 rounded bg-kind-skill/50" />
                  <span className="block h-[3px] w-10 rounded bg-kind-workflow/50" />
                  <span className="block h-[3px] w-6 rounded bg-kind-gem/50" />
                </div>
                <p className="font-display text-base font-semibold text-ink">{es.title}</p>
                <p className="text-sm text-graphite mt-1 mb-5 max-w-[34ch]">{es.text}</p>
                {es.action}
              </div>
            );
          })()
        ) : (
          prompts.map(prompt => {
            const kind = getKind(prompt);
            const meta = KIND_META[kind];
            const isSelected = selectedPromptId === prompt.id;
            const daysInTrash = prompt.deletedAt ? Math.floor((Date.now() - new Date(prompt.deletedAt).getTime()) / 86_400_000) : 0;
            const excerpt = excerptFor(prompt);

            return (
              <div
                key={prompt.id}
                role="listitem"
                data-list-item
                tabIndex={0}
                draggable={!isTrash}
                onDragStart={(e) => handleDragStart(e, prompt.id)}
                onDragEnd={() => {
                  setDraggedId(null);
                  setDropTargetId(null);
                  setDropPosition(null);
                }}
                onDragOver={(e) => {
                  if (isTrash || !draggedId || draggedId === prompt.id) return;
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pos = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
                  setDropTargetId(prompt.id);
                  setDropPosition(pos);
                }}
                onDragLeave={(e) => {
                  if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                  if (dropTargetId === prompt.id) {
                    setDropTargetId(null);
                    setDropPosition(null);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const targetFolderId = selectedFolderId || actions.getFolderId?.(prompt.id);
                  const sourcePromptId = draggedId;
                  const currentDropPos = dropPosition;
                  setDropTargetId(null);
                  setDropPosition(null);
                  setDraggedId(null);

                  if (isTrash || !sourcePromptId || sourcePromptId === prompt.id || !targetFolderId) return;

                  const targetIdxInList = prompts.findIndex(p => p.id === prompt.id);
                  const targetIndex = currentDropPos === 'after' ? targetIdxInList + 1 : targetIdxInList;
                  actions.handleMovePrompt(sourcePromptId, targetFolderId, targetIndex);
                }}
                onClick={() => {
                  if (isLongPressRef.current) {
                    isLongPressRef.current = false;
                    return;
                  }
                  setSelectedPromptId(prompt.id);
                }}
                onContextMenu={(e) => { if (!isTrash) openMenu(e, prompt); }}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedPromptId(prompt.id);
                  }
                }}
                onTouchStart={startLongPress(prompt)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                aria-current={isSelected ? 'true' : undefined}
                aria-label={`${meta.label}: ${prompt.name}${prompt.isFavorite ? ', Favorit' : ''}`}
                className={`group relative pl-5 pr-3 py-3 border-b border-rule/70 cursor-pointer transition-colors outline-offset-[-2px] ${
                  isSelected ? 'bg-raised' : 'hover:bg-ink/[0.03]'
                } ${draggedId === prompt.id ? 'opacity-40' : ''}`}
              >
                {dropTargetId === prompt.id && dropPosition === 'before' && (
                  <div className="absolute top-0 left-0 right-0 h-0.5 bg-indigo-500 z-10 pointer-events-none" />
                )}
                {dropTargetId === prompt.id && dropPosition === 'after' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-500 z-10 pointer-events-none" />
                )}
                {/* Linienfarbe der Art */}
                <span
                  className={`absolute left-2 top-3 bottom-3 rounded-full ${meta.bg} ${isSelected ? 'w-[4px] opacity-100' : 'w-[3px] opacity-70'}`}
                  aria-hidden="true"
                />

                <div className="flex items-center gap-2 text-[11px] text-graphite">
                  <span className={`flex items-center gap-1 kind-label ${meta.text}`}>
                    <KindGlyph kind={kind} className="h-3.5 w-3.5" />
                    {meta.label}
                  </span>
                  <span className="truncate min-w-0">{actions.getFolderName(prompt.id)}</span>
                  <span className="ml-auto shrink-0 font-mono tabular-nums">
                    {isTrash ? (
                      <span className={daysInTrash > TRASH_MAX_DAYS - 5 ? 'text-red-700 dark:text-red-400' : ''}>
                        noch {Math.max(0, TRASH_MAX_DAYS - daysInTrash)} T.
                      </span>
                    ) : formatDate(prompt.updatedAt)}
                  </span>
                </div>

                <div className="flex items-start gap-2 mt-1">
                  <h3 className={`flex-1 min-w-0 text-[15px] leading-snug truncate ${isSelected ? 'font-semibold text-ink' : 'font-medium text-ink'}`}>
                    <Highlight text={prompt.name || 'Ohne Titel'} highlight={searchTerm} />
                  </h3>
                  {prompt.isFavorite && !isTrash && (
                    <StarIcon filled className="h-3.5 w-3.5 mt-1 shrink-0 text-amber-500" />
                  )}
                </div>

                {excerpt && (
                  <p className="text-[13px] text-graphite line-clamp-2 leading-relaxed mt-0.5 break-words">
                    <Highlight text={excerpt.slice(0, 240)} highlight={searchTerm} />
                  </p>
                )}

                <div className="flex items-center gap-2 mt-1.5 min-w-0">
                  <span className="font-mono text-[11px] text-graphite shrink-0 truncate max-w-[60%]">{structureNote(prompt)}</span>
                  {(prompt.tags || []).slice(0, 3).map(tag => (
                    <span key={tag} className="font-mono text-[11px] text-ink/70 bg-ink/[0.05] rounded px-1.5 shrink-0">{tag}</span>
                  ))}
                  {(prompt.tags || []).length > 3 && <span className="font-mono text-[11px] text-graphite">+{prompt.tags.length - 3}</span>}
                </div>

                {/* Aktionen */}
                {isTrash ? (
                  <div className="flex items-center gap-1 mt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRestorePrompt(prompt.id); }}
                      className="btn btn-secondary btn-sm !min-h-[28px] !px-2 !text-xs"
                    >
                      <RestoreIcon className="h-3.5 w-3.5" /> Wiederherstellen
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setModalState({ type: 'delete-forever', promptId: prompt.id, promptName: prompt.name }); }}
                      className="btn btn-ghost btn-sm !min-h-[28px] !px-2 !text-xs hover:!text-red-700 dark:hover:!text-red-400"
                    >
                      <TrashIcon className="h-3.5 w-3.5" /> Endgültig löschen
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); openMenu(e, prompt); }}
                    className="absolute right-2 bottom-2 icon-btn !h-7 !w-7 bg-sheet opacity-0 group-hover:opacity-100 focus:opacity-100"
                    title="Aktionen"
                    aria-label={`Aktionen für ${prompt.name}`}
                  >
                    <MoreVerticalIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PromptList;
